/**
 * Stable customer-facing adapter over the generated public FnB operations.
 *
 * `publicFnb.ts` and `typings.d.ts` are generator-owned. This maintained adapter
 * provides readable names, capability headers, and an explicitly anonymous
 * request policy without duplicating generated DTOs.
 */

import type { RequestOptions } from '@nexacore/sdk';
import './types';
import * as generated from './publicFnb';

export type ExchangeQrRequest = API.ExchangeQrRequest;
export type CreateStoreOrderingSessionRequest = API.CreateStoreOrderingSessionRequest;
export type RecoverPublicCartRequest = API.RecoverPublicCartRequest;
export type PublicCartItemRequest = API.PublicCartItemRequest;
export type ApplyPublicVoucherRequest = API.ApplyPublicVoucherRequest;
export type SetPublicPromotionsRequest = API.SetPublicPromotionsRequest;
export type SetPublicPickupRequest = API.SetPublicPickupRequest;
export type PublicCheckoutRequest = API.PublicCheckoutRequest;
export type InitializePublicPaymentRequest = API.InitializePublicPaymentRequest;
export type CancelPublicOrderRequest = API.CancelPublicOrderRequest;
export type PublicFnbResponse = API.PublicFnbResponseResponse;
export type PublicProductModel = API.PublicProductModel;
export type PublicStoreModel = API.PublicStoreModel;
export type PublicMenuHeaderModel = API.PublicMenuHeaderModel;
export type PublicMenuItemModel = API.PublicMenuItemModel;
export type PublicMoodModel = API.PublicMoodModel;
export type PublicMoodRecommendationModel = API.PublicMoodRecommendationModel;
export type PublicEventModel = API.PublicEventModel;
export type PublicPaymentInstructionsModel = API.PublicPaymentInstructionsModel;
export type PublicPromotionOptionModel = API.PublicPromotionOptionModel;
export type PublicDeliveryAddressRequest = API.PublicDeliveryAddressRequest;
export type PublicWalkInCustomerModel = API.PublicWalkInCustomerModel;
export type CountryModel = API.CountryModel;
export type ProvinceModel = API.ProvinceModel;
export type CommuneModel = API.CommuneModel;

export interface OrderingCapabilities {
  orderingSession?: string;
  cartProof?: string;
  trackingToken?: string;
}

function anonymousOptions(
  capabilities: OrderingCapabilities = {},
  options: RequestOptions = {}
): RequestOptions {
  const headers: Record<string, string> = { ...(options.headers ?? {}) };
  if (capabilities.orderingSession) {
    headers['X-Fnb-Ordering-Session'] = capabilities.orderingSession;
  }
  if (capabilities.cartProof) headers['X-Fnb-Cart-Proof'] = capabilities.cartProof;
  if (capabilities.trackingToken) {
    headers['X-Fnb-Tracking-Token'] = capabilities.trackingToken;
  }

  return {
    ...options,
    headers,
    skipAuth: true,
    retryOn401: false,
  };
}

export const exchangeQr = (body: ExchangeQrRequest, options?: RequestOptions) =>
  generated.postPublicV1OrderingSessions(body, anonymousOptions({}, options));

export const createStoreOrderingSession = (
  body: CreateStoreOrderingSessionRequest,
  options?: RequestOptions
) => generated.postPublicV1OrderingSessionsStore(body, anonymousOptions({}, options));

export const getBestSellerProducts = (
  limit = 8,
  options?: RequestOptions,
  locale?: string,
  storeCode?: string
) =>
  generated.getPublicV1ProductsBestSellers(
    { limit, locale, storeCode },
    anonymousOptions({}, options)
  );

export const getPublicProduct = (
  productId: string,
  options?: RequestOptions,
  locale?: string,
  storeCode?: string
) =>
  generated.getPublicV1ProductsProductId(
    { productId, locale, storeCode },
    anonymousOptions({}, options)
  );

export const getPublicStores = (options?: RequestOptions, locale?: string) =>
  generated.getPublicV1Stores({ locale }, anonymousOptions({}, options));

export const getPublicCountries = (options?: RequestOptions) =>
  generated.getPublicV1AddressingCountries(anonymousOptions({}, options));

export const getPublicProvinces = (countryId: string, options?: RequestOptions) =>
  generated.getPublicV1AddressingProvinces({ countryId }, anonymousOptions({}, options));

export const getPublicCommunes = (provinceId: string, options?: RequestOptions) =>
  generated.getPublicV1AddressingCommunes({ provinceId }, anonymousOptions({}, options));

export const getPublicMenus = (storeCode: string, options?: RequestOptions, locale?: string) =>
  generated.getPublicV1Menus({ storeCode, locale }, anonymousOptions({}, options));

export const getPublicMenuItems = (
  menuId: string,
  options?: RequestOptions,
  locale?: string,
  storeCode?: string,
  limit?: number,
  page = 1
) => {
  const params: API.getPublicV1MenusMenuIdItemsParams & {
    limit?: number;
    page: number;
  } = { menuId, locale, storeCode, limit, page };
  return generated.getPublicV1MenusMenuIdItems(params, anonymousOptions({}, options));
};

export const getPublicMoods = (options?: RequestOptions, locale?: string, storeCode?: string) =>
  generated.getPublicV1Moods({ locale, storeCode }, anonymousOptions({}, options));

export const getPublicMoodItems = (
  moodCode: string,
  limit = 8,
  options?: RequestOptions,
  locale?: string,
  storeCode?: string,
  page = 1
) => {
  const params: API.getPublicV1MoodsMoodCodeItemsParams & { page: number } = {
    moodCode,
    limit,
    locale,
    storeCode,
    page,
  };
  return generated.getPublicV1MoodsMoodCodeItems(params, anonymousOptions({}, options));
};

export const getPublicEvents = (
  storeCode?: string,
  limit = 10,
  options?: RequestOptions,
  locale?: string
) => generated.getPublicV1Events({ storeCode, limit, locale }, anonymousOptions({}, options));

export const getPublicEvent = (eventId: string, options?: RequestOptions) =>
  generated.getPublicV1EventsEventId({ eventId }, anonymousOptions({}, options));

export const getMenu = (orderingSession: string, options?: RequestOptions) =>
  generated.getPublicV1OrderingSessionsMenu(anonymousOptions({ orderingSession }, options));

export const getProduct = (productId: string, orderingSession: string, options?: RequestOptions) =>
  generated.getPublicV1OrderingSessionsProductsProductId(
    { productId },
    anonymousOptions({ orderingSession }, options)
  );

export const getWalkInCustomer = (orderingSession: string, options?: RequestOptions) =>
  generated.getPublicV1OrderingSessionsWalkInCustomer(
    anonymousOptions({ orderingSession }, options)
  );

export const createCart = (orderingSession: string, options?: RequestOptions) =>
  generated.postPublicV1Carts(anonymousOptions({ orderingSession }, options));

export const recoverCart = (body: RecoverPublicCartRequest, options?: RequestOptions) =>
  generated.postPublicV1CartRecoveries(body, anonymousOptions({}, options));

export const getCart = (
  cartId: string,
  orderingSession: string,
  cartProof: string,
  options?: RequestOptions
) =>
  generated.getPublicV1CartsCartId(
    { cartId },
    anonymousOptions({ orderingSession, cartProof }, options)
  );

export const addCartItem = (
  cartId: string,
  body: PublicCartItemRequest,
  orderingSession: string,
  cartProof: string,
  options?: RequestOptions
) =>
  generated.postPublicV1CartsCartIdItems(
    { cartId },
    body,
    anonymousOptions({ orderingSession, cartProof }, options)
  );

export const updateCartItem = (
  cartId: string,
  itemId: string,
  body: PublicCartItemRequest,
  orderingSession: string,
  cartProof: string,
  options?: RequestOptions
) =>
  generated.putPublicV1CartsCartIdItemsItemId(
    { cartId, itemId },
    body,
    anonymousOptions({ orderingSession, cartProof }, options)
  );

export const removeCartItem = (
  cartId: string,
  itemId: string,
  expectedVersion: number,
  orderingSession: string,
  cartProof: string,
  options?: RequestOptions
) =>
  generated.deletePublicV1CartsCartIdItemsItemId(
    { cartId, itemId, expectedVersion },
    anonymousOptions({ orderingSession, cartProof }, options)
  );

export const applyVoucher = (
  cartId: string,
  body: ApplyPublicVoucherRequest,
  orderingSession: string,
  cartProof: string,
  options?: RequestOptions
) =>
  generated.putPublicV1CartsCartIdVoucher(
    { cartId },
    body,
    anonymousOptions({ orderingSession, cartProof }, options)
  );

export const removeVoucher = (
  cartId: string,
  code: string,
  expectedVersion: number,
  orderingSession: string,
  cartProof: string,
  options?: RequestOptions
) =>
  generated.deletePublicV1CartsCartIdVoucher(
    { cartId, code, expectedVersion },
    anonymousOptions({ orderingSession, cartProof }, options)
  );

export const getAvailablePromotions = (
  cartId: string,
  orderingSession: string,
  cartProof: string,
  options?: RequestOptions
) =>
  generated.getPublicV1CartsCartIdPromotions(
    { cartId },
    anonymousOptions({ orderingSession, cartProof }, options)
  );

export const setPromotions = (
  cartId: string,
  body: SetPublicPromotionsRequest,
  orderingSession: string,
  cartProof: string,
  options?: RequestOptions
) =>
  generated.putPublicV1CartsCartIdPromotions(
    { cartId },
    body,
    anonymousOptions({ orderingSession, cartProof }, options)
  );

export const setPickup = (
  cartId: string,
  body: SetPublicPickupRequest,
  orderingSession: string,
  cartProof: string,
  options?: RequestOptions
) =>
  generated.putPublicV1CartsCartIdPickup(
    { cartId },
    body,
    anonymousOptions({ orderingSession, cartProof }, options)
  );

export const checkout = (
  cartId: string,
  body: PublicCheckoutRequest,
  orderingSession: string,
  cartProof: string,
  options?: RequestOptions
) =>
  generated.postPublicV1CartsCartIdCheckout(
    { cartId },
    body,
    anonymousOptions({ orderingSession, cartProof }, options)
  );

export const initializePayment = (
  orderCode: string,
  body: InitializePublicPaymentRequest,
  trackingToken: string,
  options?: RequestOptions
) =>
  generated.postPublicV1OrdersOrderCodePayment(
    { orderCode },
    body,
    anonymousOptions({ trackingToken }, options)
  );

export const trackOrder = (orderCode: string, trackingToken: string, options?: RequestOptions) =>
  generated.getPublicV1OrdersOrderCodeTracking(
    { orderCode },
    anonymousOptions({ trackingToken }, options)
  );

export const cancelOrder = (
  orderCode: string,
  body: CancelPublicOrderRequest,
  trackingToken: string,
  options?: RequestOptions
) =>
  generated.postPublicV1OrdersOrderCodeCancellation(
    { orderCode },
    body,
    anonymousOptions({ trackingToken }, options)
  );
